import { FC, useMemo, useState } from 'react'
import { Statistic, Row, Col, Card, Table, Button, Flex } from 'antd'
import moment from 'moment-timezone'
import { useParams, useNavigate } from 'react-router'
import {
  FileDoneOutlined,
  LeftOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { PageSpinner, Text, Space, Title } from '@signifyd/components'
import { toTitleCase } from '@signifyd/utils'
import { spacingMD } from '@signifyd/ant'
import { useDashboardParams } from 'core/queryParams/useDashboardParams'
import PrLink from 'pages/Dashboard/PrLink'
import { useGetRuns } from 'lib/queries/useGetRuns/useGetRuns'
import { getTableData, getTestCasesColumns } from './utils'
import RunDetailsBanner from './RunDetailsBanner'
import SpecFileData from './components/SpecFileData'
import styles from './RunDetails.less'

export enum SUMMARY_MODE {
  SUMMARY_VIEW = 'summaryView',
  TEST_CASE_VIEW = 'testCaseView',
}

const RunDetails: FC = () => {
  const [view, setView] = useState<SUMMARY_MODE>(SUMMARY_MODE.SUMMARY_VIEW)
  const {
    query: { suite },
  } = useDashboardParams()

  const navigate = useNavigate()
  const { runId, projectName } = useParams<{
    projectName: string
    runId: string
  }>()

  const { data, isLoading } = useGetRuns({ suite, project: projectName })

  const summarisedData = useMemo(() => {
    const currentRun = data?.find((run) => run.runId === runId)

    if (!currentRun) {
      return null
    }

    return view === SUMMARY_MODE.SUMMARY_VIEW
      ? getTableData(currentRun)
      : currentRun
  }, [data, view, runId])

  if (!projectName || !runId) {
    return null
  }

  if (isLoading) {
    return <PageSpinner />
  }

  const navPrefix = `?suite=${suite}`
  const summaryItem =
    view === SUMMARY_MODE.SUMMARY_VIEW ? 'Tests' : 'Test cases'

  return (
    <>
      <Button
        onClick={() =>
          navigate(`/playwright/${projectName}/ci-runs${navPrefix}`)
        }
        icon={<LeftOutlined />}
      >
        {`All ${projectName} runs`}
      </Button>
      <Space size={spacingMD} />
      <RunDetailsBanner status={summarisedData?.status} />
      <Space size={spacingMD} />
      <Title level={3}>
        {`${toTitleCase(projectName)} CI ${summaryItem} summary`}
      </Title>

      <Flex>
        <Button
          icon={<FileDoneOutlined />}
          type={view === SUMMARY_MODE.SUMMARY_VIEW ? 'primary' : 'default'}
          onClick={() => setView(SUMMARY_MODE.SUMMARY_VIEW)}
        >
          Run summary
        </Button>
        <Button
          icon={<UnorderedListOutlined />}
          type={view === SUMMARY_MODE.TEST_CASE_VIEW ? 'primary' : 'default'}
          onClick={() => setView(SUMMARY_MODE.TEST_CASE_VIEW)}
        >
          Test case summary
        </Button>

        <Col span={8} />
      </Flex>

      {summarisedData && (
        <>
          <Space size={spacingMD} />
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Total ${summaryItem}`}
                  value={summarisedData.totalTests}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Passed ${summaryItem}`}
                  value={summarisedData.passed}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Failed ${summaryItem}`}
                  value={summarisedData.failed}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Skipped ${summaryItem}`}
                  value={summarisedData.skipped}
                  valueStyle={{ color: 'blue' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Flaky ${summaryItem}`}
                  value={summarisedData.flaky}
                  valueStyle={{ color: 'orange' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`Interrupted ${summaryItem}`}
                  value={summarisedData.interrupted}
                  valueStyle={{ color: 'grey' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title="Branch" value={summarisedData.branchName} />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Flex vertical>
                  <Text className={styles.prLinkTitle} size="sm">
                    Pr link
                  </Text>
                  <PrLink
                    className={styles.prLink}
                    prNumber={summarisedData.pullRequestNumber}
                  />
                </Flex>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Runtime (minutes)"
                  value={moment(
                    moment
                      .duration(summarisedData.totalDuration)
                      .asMilliseconds()
                  ).format('mm:ss')}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Timestamp"
                  value={new Date(summarisedData.datetime).toLocaleString()}
                />
              </Card>
            </Col>
          </Row>

          <Title level={4}>Spec Files for Run ID: {runId}</Title>

          <Space size={spacingMD} />

          <Table
            rowKey="specFileName"
            expandable={{
              fixed: 'left',
              expandedRowRender: (record) => (
                <SpecFileData fileName={record.specFileName} />
              ),
              rowExpandable: (record) =>
                record.status === 'failed' || record.status === 'flaky',
            }}
            columns={getTestCasesColumns(projectName, runId, navPrefix)}
            dataSource={summarisedData.specs}
            pagination={false}
            bordered
          />
        </>
      )}
    </>
  )
}

export default RunDetails
